import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowRight, Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import { getPortalEmoji } from '../utils/portalEmojis';
import { FilterSelect } from '../components/crm/CrmFilterUi';

// Tone → full class strings (spelled out so Tailwind's JIT keeps them).
const TONES = {
  orange: { chip: 'bg-orange-100/80 dark:bg-orange-500/15 ring-orange-200/70 dark:ring-orange-400/20', glow: 'bg-orange-400/40', text: 'group-hover:text-orange-600 dark:group-hover:text-orange-400' },
  red: { chip: 'bg-red-100/80 dark:bg-red-500/15 ring-red-200/70 dark:ring-red-400/20', glow: 'bg-red-400/40', text: 'group-hover:text-red-600 dark:group-hover:text-red-400' },
  rose: { chip: 'bg-rose-100/80 dark:bg-rose-500/15 ring-rose-200/70 dark:ring-rose-400/20', glow: 'bg-rose-400/40', text: 'group-hover:text-rose-600 dark:group-hover:text-rose-400' },
  pink: { chip: 'bg-pink-100/80 dark:bg-pink-500/15 ring-pink-200/70 dark:ring-pink-400/20', glow: 'bg-pink-400/40', text: 'group-hover:text-pink-600 dark:group-hover:text-pink-400' },
  green: { chip: 'bg-green-100/80 dark:bg-green-500/15 ring-green-200/70 dark:ring-green-400/20', glow: 'bg-green-400/40', text: 'group-hover:text-green-600 dark:group-hover:text-green-400' },
  yellow: { chip: 'bg-yellow-100/80 dark:bg-yellow-500/15 ring-yellow-200/70 dark:ring-yellow-400/20', glow: 'bg-yellow-400/40', text: 'group-hover:text-yellow-600 dark:group-hover:text-yellow-400' },
  blue: { chip: 'bg-blue-100/80 dark:bg-blue-500/15 ring-blue-200/70 dark:ring-blue-400/20', glow: 'bg-blue-400/40', text: 'group-hover:text-blue-600 dark:group-hover:text-blue-400' },
  amber: { chip: 'bg-amber-100/80 dark:bg-amber-500/15 ring-amber-200/70 dark:ring-amber-400/20', glow: 'bg-amber-400/40', text: 'group-hover:text-amber-600 dark:group-hover:text-amber-400' },
  slate: { chip: 'bg-slate-100/80 dark:bg-slate-500/15 ring-slate-200/70 dark:ring-slate-400/20', glow: 'bg-slate-400/40', text: 'group-hover:text-slate-600 dark:group-hover:text-slate-300' },
  violet: { chip: 'bg-violet-100/80 dark:bg-violet-500/15 ring-violet-200/70 dark:ring-violet-400/20', glow: 'bg-violet-400/40', text: 'group-hover:text-violet-600 dark:group-hover:text-violet-400' },
  indigo: { chip: 'bg-indigo-100/80 dark:bg-indigo-500/15 ring-indigo-200/70 dark:ring-indigo-400/20', glow: 'bg-indigo-400/40', text: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400' },
  teal: { chip: 'bg-teal-100/80 dark:bg-teal-500/15 ring-teal-200/70 dark:ring-teal-400/20', glow: 'bg-teal-400/40', text: 'group-hover:text-teal-600 dark:group-hover:text-teal-400' },
  sky: { chip: 'bg-sky-100/80 dark:bg-sky-500/15 ring-sky-200/70 dark:ring-sky-400/20', glow: 'bg-sky-400/40', text: 'group-hover:text-sky-600 dark:group-hover:text-sky-400' },
  emerald: { chip: 'bg-emerald-100/80 dark:bg-emerald-500/15 ring-emerald-200/70 dark:ring-emerald-400/20', glow: 'bg-emerald-400/40', text: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400' },
};

const REPORT_GROUPS = [
  {
    id: 'growth',
    emoji: '📈',
    title: 'Growth',
    reports: [
      { id: 'marketing', title: 'Marketing', emoji: getPortalEmoji('marketing'), tone: 'orange', path: '/crm/marketing-reports', description: 'Lead performance, expenditure, and ROI' },
      { id: 'sales', title: 'Sales', emoji: getPortalEmoji('sales'), tone: 'red', path: '/crm/sales-reports', description: 'Sales metrics, pipeline and conversion data' },
      { id: 'lost-leads', title: 'Leads', emoji: '🌱', tone: 'rose', path: '/crm/lost-leads-reports', description: 'All leads, won and lost, with trends and insights' },
      { id: 'lead-portal', title: 'Lead Portal', emoji: '🌐', tone: 'emerald', path: '/crm/lead-portal-reports', description: 'Start portal traffic, engagement events, and per-lead activity' },
    ],
  },
  {
    id: 'clients',
    emoji: '🤝',
    title: 'Clients',
    reports: [
      { id: 'lost-clients', title: 'Clients', emoji: '🤝', tone: 'pink', path: '/crm/lost-clients-reports', description: 'All clients, active and lost, with retention insights' },
      { id: 'client-satisfaction', title: 'Client Check Ins', emoji: '⭐', tone: 'blue', path: '/crm/client-satisfaction', description: 'Client check-in ratings, feedback, and response tracking' },
      { id: 'client-purchases', title: 'Client Purchases', emoji: '🛒', tone: 'indigo', path: '/crm/client-purchases', description: 'Client purchase history and transaction analytics' },
      { id: 'breakthroughs', title: 'Breakthroughs', emoji: '⚡', tone: 'yellow', path: '/crm/breakthroughs-reports', description: 'Key achievements, milestones, and breakthrough moments' },
    ],
  },
  {
    id: 'team',
    emoji: '🧭',
    title: 'Team & Delivery',
    reports: [
      { id: 'meetings', title: 'Meetings', emoji: '🎥', tone: 'slate', path: '/crm/meetings-reports', description: 'Saved Meeting Mode reports with the Fathom recording and action items assigned to the team' },
      { id: 'call-reports', title: 'Call Reports', emoji: '📞', tone: 'violet', path: '/crm/call-reports', description: 'Implementation Hours calls synced from Calendly + Google Calendar: by advisor, by client, and hours-log reconciliation' },
      { id: 'goals', title: 'Goals', emoji: getPortalEmoji('goals'), tone: 'yellow', path: '/crm/company-goals', description: 'Track progress on team and company goals' },
      { id: 'technology', title: 'Technology', emoji: getPortalEmoji('technology'), tone: 'sky', path: '/crm/technology-reports', description: 'Systems, infrastructure, and tooling analytics' },
      { id: 'staff-activity', title: 'Staff Activity', emoji: '📋', tone: 'indigo', path: '/crm/staff-activity', description: 'CRM logins, account views, and changes by internal team members', ownerExecOnly: true },
    ],
  },
  {
    id: 'business',
    emoji: '🏛️',
    title: 'Business & Events',
    reports: [
      // Finance is Owner / Executive only (filtered below).
      { id: 'finance', title: 'Finance', emoji: getPortalEmoji('finance'), tone: 'green', path: '/crm/finance-reports', description: 'Revenue, profit margins and expense analysis', ownerExecOnly: true },
      { id: 'summit', title: 'Summit', emoji: '🏆', tone: 'amber', path: '/crm/summit-overview', description: 'BBA Annual Summit registration and analytics' },
      { id: 'conferences', title: 'Conferences', emoji: '🎤', tone: 'teal', path: '/crm/conferences-reports', description: 'Conference attendance, presentations, and networking analytics' },
    ],
  },
];

const pageStyles = `
  @keyframes reports-rise {
    from { opacity: 0; transform: translateY(14px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .reports-rise {
    animation: reports-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .reports-tile .reports-shine {
    position: absolute;
    inset: 0;
    background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%);
    transform: translateX(-120%);
    transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }
  .dark .reports-tile .reports-shine {
    background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%);
  }
  .reports-tile:hover .reports-shine {
    transform: translateX(120%);
  }
  @media (prefers-reduced-motion: reduce) {
    .reports-rise { animation: none; }
    .reports-tile, .reports-tile * { transition: none !important; }
  }
`;

const GLASS = 'bg-white/60 dark:bg-gray-900/50 backdrop-blur-xl border border-white/70 dark:border-white/10 shadow-[0_8px_30px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.35)]';

const Reports = () => {
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (currentUser) {
        try {
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            setUserRole(internalUserDoc.data().role);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
    };
    fetchUserRole();
  }, [currentUser]);

  const canSeeFinance = userRole === 'Owner' || userRole === 'Executive';

  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return REPORT_GROUPS
      .filter((group) => groupFilter === 'all' || group.id === groupFilter)
      .map((group) => ({
        ...group,
        reports: group.reports.filter((r) => {
          if (r.ownerExecOnly && !canSeeFinance) return false;
          if (!q) return true;
          return `${r.title} ${r.description} ${group.title}`.toLowerCase().includes(q);
        }),
      }))
      .filter((group) => group.reports.length > 0);
  }, [search, groupFilter, canSeeFinance]);

  const clearFilters = () => {
    setSearch('');
    setGroupFilter('all');
  };

  // Running index so tiles stagger in order across every group.
  let tileIndex = 0;

  return (
    <div className="min-h-screen relative p-4 md:p-6 lg:p-8">
      <PortalBackground />
      <style>{pageStyles}</style>

      <div className="relative w-full">
        <header className="reports-rise mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">Reports</h1>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search reports…"
                className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white/80 dark:bg-gray-800/70 backdrop-blur text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400 transition"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <FilterSelect
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="w-full sm:w-60"
              aria-label="Filter by group"
            >
              <option value="all">All groups</option>
              {REPORT_GROUPS.map((group) => (
                <option key={group.id} value={group.id}>{group.emoji} {group.title}</option>
              ))}
            </FilterSelect>
          </div>
        </header>

        {visibleGroups.length === 0 ? (
          <div className={`reports-rise ${GLASS} rounded-3xl py-16 px-6 text-center`}>
            <div className="text-4xl mb-3" role="img" aria-hidden="true">🔍</div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{search ? <>No reports match “{search}”</> : 'No reports in this group'}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try a different keyword or group.</p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm transition"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {visibleGroups.map((group) => (
              <section key={group.id}>
                <div className="reports-rise mb-4 flex flex-col items-center text-center" style={{ animationDelay: `${tileIndex * 45}ms` }}>
                  <h2 className="inline-flex items-center gap-2 text-lg md:text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    <span role="img" aria-hidden="true">{group.emoji}</span>
                    {group.title}
                  </h2>                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
                  {group.reports.map((report) => {
                    const tone = TONES[report.tone] || TONES.slate;
                    const delay = tileIndex++ * 45;
                    return (
                      <button
                        key={report.id}
                        type="button"
                        onClick={() => navigate(report.path)}
                        style={{ animationDelay: `${delay}ms` }}
                        className={`reports-rise reports-tile group relative overflow-hidden ${GLASS} rounded-2xl p-5 md:p-6 text-center flex flex-col items-center transition-all duration-300 ease-out hover:-translate-y-1.5 hover:bg-white/80 dark:hover:bg-gray-900/70 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)] dark:hover:shadow-[0_18px_40px_rgba(0,0,0,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50`}
                      >
                        {/* Tone glow that blooms on hover */}
                        <span className={`pointer-events-none absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl ${tone.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                        <span className="reports-shine" />

                        <span className={`relative mb-4 w-16 h-16 rounded-2xl flex items-center justify-center ring-1 ${tone.chip} shadow-sm transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-3`}>
                          <span className="text-3xl leading-none" role="img" aria-hidden="true">{report.emoji}</span>
                        </span>

                        <h3 className="relative text-lg font-bold tracking-tight text-gray-900 dark:text-white">{report.title}</h3>
                        <p className="relative mt-1.5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed flex-1">{report.description}</p>

                        <span className={`relative mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 transition-colors duration-300 ${tone.text}`}>
                          Open report
                          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
